'use client';

/**
 * Explorer Panel
 *
 * @component_role Entity Navigation Hub
 * @business_rule Provides searchable, filterable lists of Projects, Vendors, and RFQs.
 *   Click an entity to open it in a tab. Active explorer view controlled by activity bar.
 *   Strategy pattern: EXPLORER_COMPONENTS maps strategy → component.
 * @user_outcome Find what you need fast - browse all procurement entities in one place
 *   instead of navigating Odoo menus.
 * @integration_point klyde_edge /neo/graph/projects, /vendors - list endpoints for entity navigation
 *
 * @layer presentation
 * @arch_component_type panel
 * @arch_domain navigation
 *
 * @component_type Navigation Panel
 * @domain Navigation
 */

import { ComponentType } from 'react';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useTotalMatrixStore } from '@/lib/stores/total-matrix-store';
import { ProjectsExplorer } from './projects-explorer';
import { VendorsExplorer } from './vendors-explorer';
import { SchemaExplorer } from './schema-explorer';
import { DocsExplorer } from './docs-explorer';
import { AgentsExplorer } from './agents-explorer';
import { TestingExplorer } from './testing-explorer';
import { SystemObeyaExplorer } from './system-obeya-explorer';
import { VendorPortalExplorer } from './vendor-portal-explorer';
import { RFIExplorer } from './rfi-explorer';
import { ExplorerCardStack } from './cards/explorer-card-stack';

// ============================================================
// EXPLORER_COMPONENTS - Local strategy → component map
// ============================================================

/**
 * Maps ExplorerStrategy values to React components.
 * Add new explorers here - no registration needed.
 */
const EXPLORER_COMPONENTS: Record<string, ComponentType> = {
  projects: ProjectsExplorer,
  vendors: VendorsExplorer,
  orders: ProjectsExplorer, // Placeholder - reuse Projects for now
  obeya: SchemaExplorer,    // Migrated — schema + ObeyaExplorerProvider
  discovery: SchemaExplorer,
  intent: SchemaExplorer,   // Phase-2 intent mode — same schema-driven explorer
  docs: DocsExplorer,
  agents: AgentsExplorer,
  testing: TestingExplorer,
  system_obeya: SystemObeyaExplorer,
  vendor_portal: VendorPortalExplorer,
  rfi: RFIExplorer,
};

export function Explorer() {
  const { activeExplorerView } = useLayoutStore();
  const getViewportConfig = useTotalMatrixStore((s) => s.getViewportConfig);
  const matrix = useTotalMatrixStore((s) => s.matrix);
  const navItems = useTotalMatrixStore((s) => s.nav);

  // Get config from Total Matrix — single source of truth
  const viewportConfig = getViewportConfig(activeExplorerView, null);
  const explorerStrategy = viewportConfig?.explorer?.strategy ?? activeExplorerView;
  const explorerLabel = viewportConfig?.explorer?.label
    ?? navItems.find((n) => n.mode === activeExplorerView)?.label
    ?? activeExplorerView;

  // Cards-everywhere explorer dispatch — when the active mode declares
  // `explorer_cards=[...]` on its `@mode` decoration, render a card
  // stack instead of dispatching to the typed EXPLORER_COMPONENTS
  // lookup. The legacy schema-driven path stays for modes that
  // haven't migrated.
  const explorerCards = matrix?.[activeExplorerView]?.explorer_cards ?? [];
  const useCardStack = explorerCards.length > 0;

  const renderContent = () => {
    if (useCardStack) {
      return <ExplorerCardStack />;
    }

    const ExplorerComponent = EXPLORER_COMPONENTS[explorerStrategy];

    if (ExplorerComponent) {
      return <ExplorerComponent />;
    }

    // Fallback to placeholder for unknown modes
    return (
      <PlaceholderExplorer
        title={explorerLabel}
        description={`${explorerLabel} coming soon`}
      />
    );
  };

  return (
    <div className="h-full flex flex-col bg-sidebar text-sidebar-foreground overflow-hidden">
      {/* Explorer Header */}
      <div className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/60 border-b border-border">
        {explorerLabel}
      </div>

      {/* Explorer Content — native overflow-auto (same pattern as copilot-panel).
          Deliberately NOT using Radix ScrollArea here because its internal
          `display: table` wrapper sizes to intrinsic content width and lets
          children escape the pane horizontally, causing clipping when the
          explorer pane is resized narrower than content minimum width. */}
      <div className="flex-1 overflow-auto overflow-x-hidden">
        {renderContent()}
      </div>
    </div>
  );
}

function PlaceholderExplorer({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="p-4 text-center">
      <div className="text-sm text-muted-foreground">{description}</div>
    </div>
  );
}
